import { useState, useEffect, useCallback } from 'react'
import { api } from '@/lib/api'

interface User {
  id: string
  email: string
  name: string
  role: string
  organizationId: string
  isPlatformAdmin?: boolean
  mfaEnabled?: boolean
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const stored = localStorage.getItem('sfcompare_user')
      return stored ? JSON.parse(stored) : null
    } catch {
      return null
    }
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Verify session via httpOnly cookie
    api.get('/api/auth/verify')
      .then(res => {
        setUser(res.data.user)
        localStorage.setItem('sfcompare_user', JSON.stringify(res.data.user))
        setLoading(false)
      })
      .catch(() => {
        setUser(null)
        localStorage.removeItem('sfcompare_user')
        setLoading(false)
      })
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.post('/api/auth/login', { email, password })
    if (res.data.mfaRequired) {
      // Return the challenge — caller handles the MFA step
      return { mfaRequired: true, mfaToken: res.data.mfaToken }
    }
    const { user: newUser } = res.data
    localStorage.setItem('sfcompare_user', JSON.stringify(newUser))
    setUser(newUser)
    return newUser
  }, [])

  const verifyMfa = useCallback(async (mfaToken: string, code: string) => {
    const res = await api.post('/api/auth/mfa/verify', { mfaToken, code })
    const { user: newUser } = res.data
    localStorage.setItem('sfcompare_user', JSON.stringify(newUser))
    setUser(newUser)
    return newUser
  }, [])

  const register = useCallback(async (email: string, password: string, name: string, organizationName: string) => {
    const res = await api.post('/api/auth/register', { email, password, name, organizationName })
    const { user: newUser } = res.data
    localStorage.setItem('sfcompare_user', JSON.stringify(newUser))
    setUser(newUser)
    return newUser
  }, [])

  const logout = useCallback(async () => {
    try { await api.post('/api/auth/logout') } catch {}
    localStorage.removeItem('sfcompare_user')
    setUser(null)
  }, [])

  return {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    verifyMfa,
    register,
    logout,
  }
}
